import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Warehouse,
  Users,
  Package,
  Package2,
  Truck,
  Receipt,
  ScrollText,
  User,
  LogOut,
  DollarSign,
  Settings,
  LayoutDashboard,
  ChevronDown,
  Building2,
  Layers,
  FolderKanban,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import client from '@/lib/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { auth as authStore } from '@/lib/api';

const NAV = {
  SUPER_ADMIN: [
    { to: '/admin', label: 'Overview', icon: LayoutDashboard, end: true },
    { to: '/admin/warehouses', label: 'Warehouses', icon: Warehouse },
    { to: '/admin/billing', label: 'Platform billing', icon: DollarSign },
    { to: '/admin/audit-logs', label: 'Audit logs', icon: ScrollText },
    { to: '/admin/profile', label: 'Profile', icon: User },
  ],
  WAREHOUSE_ADMIN: [
    { to: '/warehouse', label: 'Overview', icon: LayoutDashboard, end: true },
    { to: '/warehouse/customers', label: 'Customers', icon: Users },
    {
      id: 'masters',
      label: 'Masters',
      icon: FolderKanban,
      children: [
        { to: '/warehouse/products', label: 'Product master', icon: Package2 },
        { to: '/warehouse/categories', label: 'Category master', icon: Layers },
        { to: '/warehouse/companies', label: 'Company master', icon: Building2 },
        { to: '/warehouse/charge-masters', label: 'Charge master', icon: Receipt },
      ],
    },
    { to: '/warehouse/inventory', label: 'Inventory', icon: Package },
    { to: '/warehouse/dispatch', label: 'Dispatch', icon: Truck },
    { to: '/warehouse/invoices', label: 'Invoices', icon: Receipt },
    { to: '/warehouse/profile', label: 'Profile', icon: User },
  ],
};

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const items = NAV[user?.role] || [];

  const masterChildren = items.find((it) => it.id === 'masters')?.children || [];
  const isMasterActive = masterChildren.some((child) => location.pathname.startsWith(child.to));
  const [mastersOpen, setMastersOpen] = useState(isMasterActive);

  async function handleLogout() {
    try {
      const rToken = authStore.getRefreshToken();
      if (rToken) await client.post('/auth/logout', { refreshToken: rToken });
    } catch {
      /* ignore */
    }
    logout();
    toast.success('Signed out');
    navigate(user?.role === 'SUPER_ADMIN' ? '/admin/login' : '/warehouse/login', { replace: true });
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Sidebar */}
      <aside className="w-64 fixed left-0 top-0 bottom-0 bg-zinc-950 text-zinc-100 flex flex-col z-30" data-testid="sidebar">
        <div className="p-6 border-b border-zinc-800/70">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-white text-zinc-950 grid place-items-center font-display font-bold">S</div>
            <div>
              <div className="font-display text-base font-bold leading-none">Stockanize</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-0.5 font-mono">
                {user?.role === 'SUPER_ADMIN' ? 'Super Admin' : 'Warehouse OS'}
              </div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
          {items.map((it) => {
            if (it.children) {
              const hasActiveChild = it.children.some((c) => location.pathname.startsWith(c.to));
              return (
                <div key={it.id || it.label} className="space-y-1">
                  <button
                    type="button"
                    onClick={() => setMastersOpen((prev) => !prev)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      hasActiveChild ? 'text-white bg-zinc-900/80' : 'text-zinc-300 hover:bg-zinc-800/70 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <it.icon className="w-4 h-4" strokeWidth={1.75} />
                      <span>{it.label}</span>
                    </div>
                    <ChevronRight
                      className={`w-3.5 h-3.5 transition-transform duration-200 text-zinc-400 ${
                        mastersOpen ? 'rotate-90 text-white' : ''
                      }`}
                    />
                  </button>

                  {mastersOpen && (
                    <div className="pl-4 pr-1 py-1 space-y-0.5 ml-2 border-l border-zinc-800">
                      {it.children.map((child) => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                          className={({ isActive }) =>
                            `flex items-center gap-2.5 px-3 py-1.5 rounded-md text-xs transition-colors ${
                              isActive
                                ? 'bg-white text-zinc-950 font-semibold shadow-sm'
                                : 'text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100'
                            }`
                          }
                        >
                          <child.icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                          <span>{child.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                    isActive ? 'bg-white text-zinc-950 font-medium' : 'text-zinc-300 hover:bg-zinc-800/70 hover:text-white'
                  }`
                }
              >
                <it.icon className="w-4 h-4" strokeWidth={1.75} />
                <span>{it.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-zinc-800/70">
          <div className="flex items-center gap-1 opacity-70 text-[10px] uppercase tracking-widest font-mono">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            SYSTEM ONLINE
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="ml-64 min-h-screen flex flex-col">
        <header className="h-16 border-b border-zinc-200 bg-white flex items-center justify-between px-8 sticky top-0 z-20">
          <div className="text-sm text-zinc-500 font-mono uppercase tracking-widest">
            {user?.role === 'SUPER_ADMIN' ? 'Platform control' : user?.warehouseId ? 'Warehouse operations' : 'Warehouse'}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-zinc-50 transition-colors" data-testid="user-menu-trigger">
              <div className="w-8 h-8 rounded-full bg-zinc-950 text-white grid place-items-center text-xs font-bold font-display">
                {(user?.fullName || user?.email || '?').slice(0, 1).toUpperCase()}
              </div>
              <div className="text-left hidden md:block">
                <div className="text-sm font-medium leading-none text-zinc-950">{user?.fullName || 'User'}</div>
                <div className="text-[11px] text-zinc-500 font-mono mt-0.5">{user?.email}</div>
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-zinc-400" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-xs text-zinc-500 uppercase tracking-wider font-mono">
                {user?.role === 'SUPER_ADMIN' ? 'Super Admin' : 'Warehouse Admin'}
              </DropdownMenuLabel>
              <DropdownMenuItem onClick={() => navigate(user?.role === 'SUPER_ADMIN' ? '/admin/profile' : '/warehouse/profile')} data-testid="user-menu-profile">
                <User className="w-4 h-4 mr-2" /> Profile & settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600" data-testid="user-menu-logout">
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 p-8 max-w-7xl w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
